import { Component, ComponentFactoryResolver, OnInit, ViewChild } from '@angular/core';
import { Models } from '@ibexfusion/models';

import { ActivatedRoute, Router } from '@angular/router';

import { SnackbarService } from '@services/snackbar/snackbar.service';
import { FormDirective } from '../../../directives/form/form.directive';
import { PageAction } from '../../../components/page-actions/page-actions.component';
import { CodeSnippetService } from '@services/code-snippets/code-snippet.service';
import { LegacyCompositionService } from '@services/legacy-compositions/legacy-composition.service';
import { ImportService } from '@services/imports/import.service';
import { ImportTypeService } from '@services/import-types/import-type.service';
import { DefaultFormComponent } from '../../../components/default-form/default-form.component';
import { FormComponent } from '../../../components/default-form/form.component';
import { DataTableEvent } from '../../../components/data-table/data-table.component';

@Component({
    selector: 'app-legacy-composition-edit',
    templateUrl: './legacy-composition-edit.component.html',
    styleUrls: ['./legacy-composition-edit.component.scss'],
    providers: [LegacyCompositionService, CodeSnippetService, ImportService, ImportTypeService],
    standalone: false
})
export class LegacyCompositionEditComponent implements OnInit {

  @ViewChild(FormDirective, { static: true }) formHost: FormDirective;
  a
  public title = 'Create a new Legacy Composition';

  public subtitle: string;

  public pageActions: PageAction[];

  legacyComposition: Models.LegacyComposition;

  legacyCompositionId: string;
  legacyCompositionCodeSnippets = [];
  legacyCompositionImportIds = [];

  isLoading = false;
  showImportTable = false;
  showSnippetsTable = false;

  options = { hideFields: false };

  public columnsImportType = [
    { prop: 'title' }
  ];

  public columnsImport = [
    { prop: 'title' },
    { validate: '_id', name: 'Imported', icon: 'check_box' }
  ];

  public paramsImport: any;

  public columnsCodeSnippets = [
    { prop: 'title', name: 'Title' },
    { prop: 'type', name: 'Type' },
    { validate: 'name', name: 'Imported', icon: 'check_box' }
  ];

  constructor(
    public codeSnippetService: CodeSnippetService,
    public legacyCompositionService: LegacyCompositionService,
    public importService: ImportService,
    public importTypeService: ImportTypeService,
    private route: ActivatedRoute,
    private router: Router,
    private snackbar: SnackbarService,
    private componentFactoryResolver: ComponentFactoryResolver
  ) {
  }

  ngOnInit() {
    this.isLoading = true;
    if (this.route.snapshot.params['id']) {
      this.legacyCompositionId = this.route.snapshot.params['id'];
      this.initExisting();
    } else {
      this.initNew();
    }
    this.createPageActions();
  }

  onSave() {
    return this.legacyComposition.validate(true).then(result => {
      if (result.success) {
        if (this.legacyCompositionId) {
          this.legacyCompositionService.update(this.legacyCompositionId, this.legacyComposition).then(legacyCompositionResult => {
            this.snackbar.success('Saved changes successfull');
          });
        } else {
          this.legacyCompositionService.create(this.legacyComposition).then(legacyCompositionResult => {
            this.afterSave(legacyCompositionResult);
            this.snackbar.success('Saved new entry successfull');
          });
        }
      } else {
        console.log(result);
        this.snackbar.open('Could not create the Snippet, please fill in the form correctly');
      }
    });
  }

  private initExisting() {
    this.legacyCompositionService.get(this.legacyCompositionId).then(legacyComposition => {
      this.legacyComposition = legacyComposition;
      this.title = 'Edit Legacy Composition: ' + this.legacyComposition.title;
      if (this.legacyComposition.codeSnippetNames) {
        this.legacyCompositionCodeSnippets = this.legacyComposition.codeSnippetNames.split('\n');
      }
      if (this.legacyComposition.importIds) {
        this.legacyCompositionImportIds = this.legacyComposition.importIds.split('\n');
      }
      this.loadForm();
    });
  }

  private initNew() {
    this.isLoading = false;
    this.legacyComposition = <Models.LegacyComposition>Models.ModelFactory.createModelInstanceByName('LegacyComposition');
    this.loadForm();
  }

  private loadForm() {
    const formItem = DefaultFormComponent;
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(formItem);
    const viewContainerRef = this.formHost.viewContainerRef;
    viewContainerRef.clear();
    const componentRef = viewContainerRef.createComponent(componentFactory);
    (<FormComponent>componentRef.instance).entry = this.legacyComposition;
    (<FormComponent>componentRef.instance).options = this.options;
    this.isLoading = false;
  }

  private createPageActions(): void {
    this.pageActions = [
      new PageAction({
        icon: 'subdirectory_arrow_left',
        title: 'Go to Overview',
        callback: () => {
          this.router.navigate(['legacy-compositions']);
        }
      })
    ];
    if (this.legacyCompositionId) {
      this.pageActions.push(
        new PageAction({
          icon: 'format_paint',
          title: 'Detail View Legacy Composition',
          callback: () => {
            this.router.navigate(['legacy-compositions/', this.legacyCompositionId]);
          }
        })
      );
    }
  }

  public afterSave(legacyComposition) {
    this.legacyCompositionId = legacyComposition._id;
    this.initExisting();
    // should also change the url, but if possible without reload
  }

  public onCustomImportType(event: DataTableEvent) {
    this.paramsImport = { typeId: event.row._id };
  }

  public onCustomImport(event: DataTableEvent) {
    const index = this.legacyCompositionImportIds.indexOf(event.row._id);

    if (index > -1) {
      this.legacyCompositionImportIds.splice(index, 1);
    } else {
      this.legacyCompositionImportIds.push(event.row._id);
    }
    this.legacyComposition.importIds = this.legacyCompositionImportIds.join('\n');
  }

  public onCustomCodeSnippet(event: DataTableEvent) {
    const index = this.legacyCompositionCodeSnippets.indexOf(event.row.name);

    if (index > -1) {
      this.legacyCompositionCodeSnippets.splice(index, 1);
    } else {
      this.legacyCompositionCodeSnippets.push(event.row.name);
    }
    this.legacyComposition.codeSnippetNames = this.legacyCompositionCodeSnippets.join('\n');
  }
}
